import React, { useEffect, useMemo, useState } from 'react';
import { ClienteConsolidado } from '../types';
import type { RecorteDaTela } from '../lib/perfisExportacao';
import { formatCurrency, formatKwh } from '../utils';
import { Search, User, MapPin, Calendar, CheckCircle2, AlertTriangle, FileText, Zap, HandCoins } from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import CobrancaUnificadaModal from '../components/CobrancaUnificadaModal';

interface Props {
  clientes: ClienteConsolidado[];
  defaultClientName?: string | null;
  /** Reporta ao cabeçalho o cliente aberto, para o Excel sair só dele. */
  onRecorteChange?: (recorte: RecorteDaTela) => void;
}

const PerfilCliente360: React.FC<Props> = ({ clientes, defaultClientName, onRecorteChange }) => {
  const { profile } = useAuth();
  const [busca, setBusca] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState<ClienteConsolidado | null>(null);
  const [cobrancaUnificadaAberta, setCobrancaUnificadaAberta] = useState(false);

  // Gerar cobrança emite boleto e cria negociação — mesma faixa de papéis que
  // administra o financeiro. A RLS das tabelas repete a regra; isto é só a UX.
  const podeNegociar = ['adm', 'gerente', 'analista'].includes(profile?.role || '');

  React.useEffect(() => {
    if (defaultClientName) {
      const client = clientes.find(c => c.nome === defaultClientName);
      if (client) {
        setClienteSelecionado(client);
        setBusca('');
      }
    }
  }, [defaultClientName, clientes]);

  // Com um cliente aberto, exportar deve sair só dele — é o dossiê na tela.
  // Sem ninguém aberto, o recorte é a carteira inteira.
  const recorte: RecorteDaTela = useMemo(() => ({
    ids: clienteSelecionado ? [clienteSelecionado.nome] : clientes.map(c => c.nome),
    descricao: clienteSelecionado ? `cliente aberto na tela: ${clienteSelecionado.nome}` : '',
    totalSemFiltro: clientes.length,
  }), [clienteSelecionado, clientes]);

  useEffect(() => { onRecorteChange?.(recorte); }, [recorte, onRecorteChange]);

  const filtroBusca = busca.toLowerCase();
  const resultados = busca.length > 2 
    ? clientes.filter(c => c.nome.toLowerCase().includes(filtroBusca) || c.ucs.some(u => u.includes(filtroBusca))).slice(0, 5)
    : [];

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Perfil 360º do Cliente</h2>
        <p className="text-sm text-gray-500">Dossiê completo para tomada de decisão e negociação</p>
      </div>

      <div className="mb-6 relative max-w-md">
        <div className="relative">
          <input 
            type="text" 
            placeholder="Buscar por Razão Social ou UC..." 
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-[#5F6C37] focus:border-[#5F6C37] shadow-sm text-sm"
          />
          <Search size={18} className="absolute left-3 top-3.5 text-gray-400" />
        </div>
        
        {resultados.length > 0 && !clienteSelecionado && (
          <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
            {resultados.map(c => (
              <button
                key={c.nome}
                onClick={() => { setClienteSelecionado(c); setBusca(''); }}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
              >
                <p className="font-bold text-sm text-gray-800">{c.nome}</p>
                <p className="text-xs text-gray-500">UCs: {c.ucs.join(', ')}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {clienteSelecionado ? (
        <div className="flex-1 overflow-y-auto">
          {/* Cabeçalho do Cliente */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6 flex items-start gap-5">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
              <User size={32} className="text-gray-400" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-black text-gray-800">{clienteSelecionado.nome}</h1>
                  <div className="flex items-center gap-4 mt-2 text-xs font-semibold text-gray-600">
                    <span className="flex items-center gap-1"><MapPin size={14} className="text-gray-400"/> {clienteSelecionado.cidades.join(', ')}</span>
                    <span className="flex items-center gap-1"><Calendar size={14} className="text-gray-400"/> Vencimento Mais Antigo: {clienteSelecionado.vencMaisAntigo ? new Date(clienteSelecionado.vencMaisAntigo).toLocaleDateString('pt-BR') : 'N/A'}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    clienteSelecionado.score === 'A' ? 'bg-green-100 text-green-700' : 
                    clienteSelecionado.score === 'B' ? 'bg-blue-100 text-blue-700' : 
                    clienteSelecionado.score === 'C' ? 'bg-yellow-100 text-yellow-700' : 
                    'bg-red-100 text-red-700'
                  }`}>
                    Score {clienteSelecionado.score}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    clienteSelecionado.risco.includes('ESTRATÉGICO') ? 'bg-red-600 text-white' : 
                    clienteSelecionado.risco.includes('ALTO') ? 'bg-orange-500 text-white' : 
                    clienteSelecionado.risco.includes('MÉDIO') ? 'bg-yellow-500 text-white' : 
                    'bg-green-500 text-white'
                  }`}>
                    {clienteSelecionado.risco}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-gray-500">
                <AlertTriangle size={16} /> <span className="text-xs font-bold uppercase tracking-wider">Dívida Total</span>
              </div>
              <p className="text-3xl font-black text-red-600">{formatCurrency(clienteSelecionado.valorTotal)}</p>
              <p className="text-xs text-gray-500 mt-2">{clienteSelecionado.qtdFaturas} faturas em aberto</p>
            </div>
            
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-gray-500">
                <Zap size={16} /> <span className="text-xs font-bold uppercase tracking-wider">Saldo Retido</span>
              </div>
              <p className="text-3xl font-black text-blue-600">{formatKwh(clienteSelecionado.saldoAcumulado)}</p>
              <p className="text-xs text-gray-500 mt-2">Crédito de energia bloqueado</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-gray-500">
                <CheckCircle2 size={16} /> <span className="text-xs font-bold uppercase tracking-wider">Perfil Comportamental</span>
              </div>
              <p className="text-lg font-bold text-gray-800">
                {clienteSelecionado.curvaAbc === 'A' ? 'Inadimplente Recorrente' : 'Inadimplente Esporádico'}
              </p>
              <p className="text-xs text-gray-500 mt-2">Análise baseada em histórico local</p>
            </div>
          </div>

          {/* Faturas */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-gray-500" />
                <h3 className="font-bold text-gray-800">Faturas em Aberto ({clienteSelecionado.faturas.length})</h3>
              </div>
              {/* Sem condicionar à quantidade que ESTA tela mostra: aqui só entram
                  as vencidas em produção da carteira, e o modal procura mais que
                  isso (em aberto + vencidas, de todas as UCs do titular). Um
                  cliente com 1 vencida e 3 em aberto tem o que unificar e ficava
                  sem o botão. Quem barra seleção pequena é a validação do modal. */}
              {podeNegociar ? (
                <button
                  onClick={() => setCobrancaUnificadaAberta(true)}
                  title="Junta as faturas num boleto só; quando ele for pago, elas viram Quitado automaticamente"
                  className="px-3 py-2 bg-[#5F6C37] text-white text-xs font-bold rounded-lg hover:bg-[#4d5830] transition-colors flex items-center gap-2"
                >
                  <HandCoins size={14} /> Cobrança unificada
                </button>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 uppercase bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-5 py-3 font-bold">Mês Referência</th>
                    <th className="px-5 py-3 font-bold">Unidade Consumidora</th>
                    <th className="px-5 py-3 font-bold">Vencimento</th>
                    <th className="px-5 py-3 font-bold text-right">Valor</th>
                    <th className="px-5 py-3 font-bold text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {clienteSelecionado.faturas.map(f => (
                    <tr key={f.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-3 font-semibold text-gray-700">{f.mes_referencia}</td>
                      <td className="px-5 py-3 text-gray-600">{f.uc_codigo}</td>
                      <td className="px-5 py-3 text-gray-600">{f.vencimento_boleto || f.data_vencimento ? new Date(f.vencimento_boleto || f.data_vencimento).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className="px-5 py-3 text-right font-black text-gray-800">{formatCurrency(f.valor_boleto_vigor)}</td>
                      <td className="px-5 py-3 text-center">
                        <span className="px-2 py-0.5 text-[10px] rounded bg-red-50 text-red-600 font-bold border border-red-100">Vencido</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center bg-white border border-gray-200 rounded-xl border-dashed">
          <User size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Busque e selecione um cliente acima para visualizar o dossiê completo.</p>
        </div>
      )}

      {cobrancaUnificadaAberta && clienteSelecionado ? (
        <CobrancaUnificadaModal
          isOpen
          onClose={() => setCobrancaUnificadaAberta(false)}
          clienteNome={clienteSelecionado.nome}
          clienteCpfCnpj={clienteSelecionado.cpfCnpj}
        />
      ) : null}
    </div>
  );
};

export default PerfilCliente360;
